import { CheckCircledIcon, CrossCircledIcon } from '@radix-ui/react-icons';
import { Button, Flex, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { CommonCallout } from 'components/common/callouts';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonProgress } from 'components/common/progress';
import { MachineContext } from 'contexts/machine.context';
import { IntercomEvents } from 'enums/intercom.enums';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { CalibrateProc } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  ICalibrateProgressMsg,
  ICalibrateResponseMsg,
} from 'lib_ts/interfaces/machine-msg/i-calibrate';
import { clamp } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { MachinesService } from 'services/machines.service';
import { useAuthStore } from 'stores/use-auth-store';
import useMachineStatusStore from 'stores/use-machine-status-store';

interface IProps {
  onClose: () => void;
}

export const RealignMachineDialog = (props: IProps) => {
  const wsConnected = useAuthStore((s) => s.wsConnected);
  const { machine, update, calibrate } = useContext(MachineContext);

  // range: 0-1
  const [progressPercent, setProgressPercent] = useState(0);
  const [resultMsg, setResultMsg] = useState<ICalibrateResponseMsg>();

  // if we have a result, assumption is we're done so progress should be 100%
  const progress = (resultMsg ? 1 : progressPercent) * 100;

  // listen to progress msgs
  useEffect(() => {
    if (!wsConnected) {
      return;
    }

    const callback = async (event: CustomEvent) => {
      const data: ICalibrateProgressMsg = event.detail;

      const countCompleted = data.progress_list.filter(
        (p) => p.success !== undefined
      ).length;

      // avoid dividing by 0
      const total = data.progress_list.length || 1;

      setProgressPercent(clamp(countCompleted / total, 0, 1));

      if (data.progress_list.findIndex((p) => p.success === undefined) === -1) {
        // everything is done calibrating or failed to calibrate
        const failed = data.progress_list.filter((p) => p.success === false);

        if (failed.length > 0) {
          NotifyHelper.error({
            inbox: true,
            message_md:
              'Your machine has failed to realign. Please contact support.',
            buttons: [
              {
                label: 'common.contact-support',
                dismissAfterClick: true,
                onClick: () => {
                  document.dispatchEvent(
                    new CustomEvent(IntercomEvents.ShowNewMessage, {
                      detail: undefined,
                    })
                  );
                },
              },
            ],
          });
          return;
        }

        const { machineID } = machine;

        const { machine: updatedMachine, calibrated } =
          await MachinesService.getInstance().getByMachineID(machineID);

        update(updatedMachine);

        if (calibrated) {
          NotifyHelper.success({
            message_md: 'Calibration finished successfully!',
          });
        }

        useMachineStatusStore.setState({ calibrated: calibrated });
      }
    };

    WebSocketHelper.on(WsMsgType.M2U_CalibrateProgress, callback);

    return () => {
      WebSocketHelper.remove(WsMsgType.M2U_CalibrateProgress, callback);
    };
  }, [wsConnected]);

  // listen to completions msgs
  useEffect(() => {
    const callback = (event: CustomEvent) => {
      const data: ICalibrateResponseMsg = event.detail;

      if (data.status === undefined) {
        // shouldn't trigger, but just in case old FW reports before completion
        return;
      }

      setResultMsg(data);
      setProgressPercent(1);
    };

    WebSocketHelper.on(WsMsgType.M2U_CalibrationResponse, callback);

    return () => {
      WebSocketHelper.remove(WsMsgType.M2U_CalibrationResponse, callback);
    };
  });

  // instruct the machine to start calibrating
  useEffect(() => {
    calibrate([CalibrateProc.All], 'RealignMachineDialog mount');
  }, []);

  return (
    <ErrorBoundary identifier="RealignMachineDialog">
      <CommonDialog
        identifier="ConfirmRealignment"
        title="Realign Machine"
        width={RADIX.DIALOG.WIDTH.SM}
        content={
          <Flex direction="column" gap="2">
            <Flex justify="between">
              <Text color={RADIX.COLOR.SECONDARY}>Progress</Text>
              <Text
                color={RADIX.COLOR.SECONDARY}
              >{`${progress.toFixed(0)}%`}</Text>
            </Flex>

            <CommonProgress value={progress} />

            {!resultMsg && (
              <Text>
                This may take a few minutes. Ensure the track is clear as the
                machine may move to the 3rd base side.
              </Text>
            )}

            {resultMsg && resultMsg.status && (
              <CommonCallout
                icon={<CheckCircledIcon />}
                color={RADIX.COLOR.SUCCESS}
                text="Machine realignment complete, please try firing a test pitch before continuing your session."
              />
            )}

            {resultMsg && !resultMsg.status && (
              <CommonCallout
                icon={<CrossCircledIcon />}
                color={RADIX.COLOR.DANGER}
                text="Machine realignment failed, please try again or contact support."
              />
            )}

            <Flex justify="end">
              <Button
                disabled={!resultMsg}
                color={
                  resultMsg
                    ? resultMsg.status
                      ? RADIX.COLOR.ACCENT // done
                      : RADIX.COLOR.DANGER // retry
                    : RADIX.COLOR.NEUTRAL // waiting for result
                }
                onClick={() => {
                  switch (resultMsg?.status) {
                    // done
                    case true:
                      props.onClose();
                      return;

                    // retry
                    case false:
                      // reset the result
                      setResultMsg(undefined);

                      // restart the calibration
                      calibrate(
                        [CalibrateProc.All],
                        'RealignMachineDialog retry'
                      );
                      return;

                    // in progress
                    default:
                      return;
                  }
                }}
              >
                {resultMsg?.status === false ? 'Retry' : 'Done'}
              </Button>
            </Flex>
          </Flex>
        }
        onClose={props.onClose}
      />
    </ErrorBoundary>
  );
};
